import React, { useEffect, useMemo, useRef, useState } from "react";
import { MonitorCounts, MonitorFilterOption, MonitorMeta, MonitorQueryState } from "./types";

interface FilterBarProps {
  meta: MonitorMeta;
  counts: MonitorCounts;
  queryState: MonitorQueryState;
  filterOptions: MonitorFilterOption[];
  // HHMCOVID-5726 — Patient Status options, built dynamically from
  // meta.patient_status_labels (see state.ts#buildPatientStatusOptions).
  patientStatusOptions: MonitorFilterOption[];
  onPatchQuery: (patch: Partial<MonitorQueryState>, options?: { resetPage?: boolean }) => void;
}

// HHMCOVID-5726 — renamed from STATUS_FILTERS; Suspended and Consent Missing
// moved to the new independent Patient Status facet (queryState.patientStatuses).
const ACTION_ITEM_FILTERS = ["needs_review", "active_tasks"];
const SEVERITY_FILTERS = ["high", "moderate", "low", "missed", "technical"];
const MONITOR_TYPE_FILTERS = ["track_and_trend", "triage"];

const LABEL_KEYS: Record<string, string> = {
  needs_review: "need_review",
  active_tasks: "active_tasks",
  high: "high_severity",
  moderate: "moderate_severity",
  low: "low_severity",
  missed: "missed_severity",
  technical: "technical_severity",
  track_and_trend: "track_and_trend",
  triage: "triage"
};

const DIRECT_COUNT_KEYS: Record<string, keyof MonitorCounts> = {
  needs_review: "need_review",
  active_tasks: "active_tasks",
  triage: "triage",
  track_and_trend: "track_and_trend"
};

function mergeFilter(filters: string[], filterName: string, selected: boolean): string[] {
  if (selected) {
    return filters.includes(filterName) ? filters : filters.concat([filterName]);
  }
  return filters.filter((name) => name !== filterName);
}

// PR #398 review — order-independent set comparison, used to skip committing
// pending filter state when closing the menu changed nothing.
function sameMembers(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((item) => setB.has(item));
}

// PR #398 review — queryState is loaded from session storage/URL, which isn't
// validated against the currently-known option set (e.g. stale data
// referencing a value that's since been renamed or removed). Without this, an
// inert value could inflate the active-filter badge/summary despite having no
// actual effect (the backend already drops anything outside its own enum).
// Also de-duplicates (preserving first-seen order) — messy stored/URL state
// like "monitoring_active,monitoring_active" would otherwise double-count in
// the badge and "Filtered by" summary.
function sanitizeAgainstOptions(values: string[], options: MonitorFilterOption[]): string[] {
  const valid = new Set(options.map((option) => option.value));
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    if (valid.has(value) && !seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
}

function countValue(counts: MonitorCounts, key: string): number {
  const directCountKey = DIRECT_COUNT_KEYS[key];
  if (directCountKey) {
    return (counts as any)[directCountKey] || 0;
  }
  if (counts.severities && counts.severities[key] != null) {
    return counts.severities[key];
  }
  if (counts.patient_statuses && counts.patient_statuses[key] != null) {
    return counts.patient_statuses[key];
  }
  return 0;
}

export default function FilterBar(props: FilterBarProps) {
  const { meta, counts, queryState, filterOptions, patientStatusOptions, onPatchQuery } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // HHMCOVID-5726 AC1 — "applies immediately when the user clicks Done", not on
  // every checkbox click. Checkbox toggles only update this local pending state;
  // it's committed to the real query (onPatchQuery) when the menu closes, for
  // ANY reason (Done, outside click, or re-clicking the toggle button) — mirrors
  // the legacy jQuery multiselect widget's own `close` callback, which applies on
  // any close, not just its "Done" link. This was a pre-existing gap for every
  // filter group (Action Items, Severity, Monitor Type), not just Patient Status;
  // fixed here across the board rather than only for the new facet.
  const [pendingFilters, setPendingFilters] = useState<string[]>(() => sanitizeAgainstOptions(queryState.filters, filterOptions));
  const [pendingPatientStatuses, setPendingPatientStatuses] = useState<string[]>(() =>
    sanitizeAgainstOptions(queryState.patientStatuses, patientStatusOptions)
  );
  const pendingRef = useRef({ filters: pendingFilters, patientStatuses: pendingPatientStatuses });
  pendingRef.current = { filters: pendingFilters, patientStatuses: pendingPatientStatuses };

  // PR #398 review — kept in a ref (not read directly from the `queryState` prop
  // inside commitPending) because commitPending is also called from the
  // outside-click effect below, whose listener closure is fixed at mount; reading
  // the prop there directly would compare against stale initial state forever.
  const queryStateRef = useRef(queryState);
  queryStateRef.current = queryState;

  const commitPending = () => {
    const current = queryStateRef.current;
    // PR #398 review — skip the query patch (and its page reset) when closing
    // the menu didn't actually change anything, e.g. opening and closing with
    // no edits, or toggling a box and toggling it back before closing.
    if (
      sameMembers(pendingRef.current.filters, current.filters) &&
      sameMembers(pendingRef.current.patientStatuses, current.patientStatuses)
    ) {
      return;
    }

    onPatchQuery(
      { filters: pendingRef.current.filters, patientStatuses: pendingRef.current.patientStatuses },
      { resetPage: true }
    );
  };

  const openMenu = () => {
    setPendingFilters(sanitizeAgainstOptions(queryState.filters, filterOptions));
    setPendingPatientStatuses(sanitizeAgainstOptions(queryState.patientStatuses, patientStatusOptions));
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    commitPending();
  };

  useEffect(() => {
    const onOutsideClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setMenuOpen((open) => {
          if (open) {
            commitPending();
          }
          return false;
        });
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optionByValue = useMemo(() => {
    const map = new Map<string, MonitorFilterOption>();
    filterOptions.forEach((option) => map.set(option.value, option));
    patientStatusOptions.forEach((option) => map.set(option.value, option));
    return map;
  }, [filterOptions, patientStatusOptions]);

  const labelFor = (value: string): string => {
    const key = LABEL_KEYS[value];
    const filtersMap = meta.filter_labels;
    if (key && filtersMap && (filtersMap as any)[key]) {
      return (filtersMap as any)[key];
    }
    const fallback = optionByValue.get(value);
    return fallback?.label ?? value;
  };

  const actionItemFilters = ACTION_ITEM_FILTERS.map((key) => optionByValue.get(key)).filter((item): item is MonitorFilterOption => !!item);
  const severityFilters = SEVERITY_FILTERS.map((key) => optionByValue.get(key)).filter((item): item is MonitorFilterOption => !!item);
  const monitorTypeFilters = MONITOR_TYPE_FILTERS.map((key) => optionByValue.get(key)).filter((item): item is MonitorFilterOption => !!item);

  const knownFilterSet = new Set([...ACTION_ITEM_FILTERS, ...SEVERITY_FILTERS, ...MONITOR_TYPE_FILTERS]);
  const extraFilters = filterOptions.filter((option) => !knownFilterSet.has(option.value));

  const strings = meta.grid_strings || ({} as any);
  const filterLabel = strings.filter_label || "Filtered by";
  const numSelectedThreshold = Number(strings.num_selected_threshold || 8);
  const numSelectedTemplate = strings.num_selected || "# Selected";
  const initialText = strings.filter_initial_text || "Click to filter Patients...";
  const filterClear = strings.filter_clear || "Clear";
  const filterDone = strings.filter_done || "Done";
  const filterStatusHeader = strings.filter_status || "Filter Patients by Action Items";
  const filterPatientStatusHeader = strings.filter_patient_status || "Filter Patients by Patient Status";
  const filterSeverityHeader = strings.filter_severity || "Filter Patients by Severity";
  const filterMonitorTypeHeader = strings.filter_monitor_type || "Filter Patients by Monitor Type";

  // HHMCOVID-5726 AC2 — Action Items and Patient Status are independent facets
  // (pendingFilters / pendingPatientStatuses), but both count toward the
  // combined "active filters" summary and badge. Reflects live checkbox state
  // while the menu is open, and the last-committed state while closed (they're
  // equal at that point — see commitPending).
  const allSelected = [...pendingFilters, ...pendingPatientStatuses];

  const buttonText = (() => {
    const selectedCount = allSelected.length;
    if (selectedCount === 0) {
      return initialText;
    }
    if (selectedCount > numSelectedThreshold) {
      return numSelectedTemplate.replace(/#/g, String(selectedCount));
    }
    const tokens = allSelected.map((name) => `${labelFor(name)} (${countValue(counts, name)})`);
    return `${filterLabel}: ${tokens.join(", ")}`;
  })();

  const onToggleFilter = (name: string, checked: boolean) => {
    setPendingFilters((current) => mergeFilter(current, name, checked));
  };

  const onTogglePatientStatus = (name: string, checked: boolean) => {
    setPendingPatientStatuses((current) => mergeFilter(current, name, checked));
  };

  const renderFilterRow = (filter: MonitorFilterOption, checked: boolean, onChange: (checked: boolean) => void) => {
    const count = countValue(counts, filter.value);
    return (
      <li key={filter.value}>
        <label className={checked ? "ui-state-active" : ""}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>{`${labelFor(filter.value)} (${count})`}</span>
        </label>
      </li>
    );
  };

  // Action Items, Severity, Monitor Type, and any unrecognised extras all live
  // in the single flat pendingFilters array (OR'd within each group, AND'd
  // across groups on the backend). Only Patient Status is separate.
  const renderMainRow = (filter: MonitorFilterOption) =>
    renderFilterRow(filter, pendingFilters.includes(filter.value), (checked) => onToggleFilter(filter.value, checked));

  const renderPatientStatusRow = (filter: MonitorFilterOption) =>
    renderFilterRow(filter, pendingPatientStatuses.includes(filter.value), (checked) => onTogglePatientStatus(filter.value, checked));

  return (
    <div className="main-filter-wrap" ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="ui-multiselect main-filter form-control"
        aria-haspopup="listbox"
        aria-expanded={menuOpen ? "true" : "false"}
        onClick={() => (menuOpen ? closeMenu() : openMenu())}
      >
        <span>{buttonText}</span>
      </button>
      {/* HHMCOVID-5726 — active-filter count badge (tech note: "badge count/highlight for active filters"),
          additive to the existing text summary above. Positioned absolutely against
          main-filter-wrap (not inline in the button) so it renders as a corner chip
          regardless of how the form-control button's text wraps. */}
      {allSelected.length > 0 && (
        <span
          className="main-filter-active-count-badge"
          aria-label={`${allSelected.length} active filters`}
          style={{
            position: "absolute",
            top: -6,
            left: -6,
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            borderRadius: 9,
            background: "#337ab7",
            color: "#fff",
            fontSize: 11,
            lineHeight: "18px",
            textAlign: "center",
            boxShadow: "0 0 0 1px #fff",
            pointerEvents: "none"
          }}
        >
          {allSelected.length}
        </span>
      )}

      {menuOpen && (
        <div
          className="ui-multiselect-menu ui-widget"
          style={{ display: "block", position: "absolute", zIndex: 1000, top: "100%", left: 0, right: 0, marginTop: 2, minWidth: 320, background: "#fff", border: "1px solid #d1d1d1", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
        >
          <div className="ui-widget-header ui-multiselect-header">
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              <li>
                <a
                  className="ui-multiselect-close"
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    closeMenu();
                  }}
                >
                  <span className="ui-icon ui-icon-circle-check" />
                  {filterDone}
                </a>
              </li>
              <li>
                <a
                  className="ui-multiselect-none"
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setPendingFilters([]);
                    setPendingPatientStatuses([]);
                  }}
                >
                  <span className="ui-icon ui-icon-closethick" />
                  {filterClear}
                </a>
              </li>
            </ul>
            <div style={{ clear: "both" }} />
          </div>

          <ul className="ui-multiselect-checkboxes ui-helper-reset">
            {actionItemFilters.length > 0 && (
              <li className="ui-multiselect-optgroup-label">
                <a>{filterStatusHeader}</a>
              </li>
            )}
            {actionItemFilters.map(renderMainRow)}

            {patientStatusOptions.length > 0 && (
              <li className="ui-multiselect-optgroup-label">
                <a>{filterPatientStatusHeader}</a>
              </li>
            )}
            {patientStatusOptions.map(renderPatientStatusRow)}

            {severityFilters.length > 0 && (
              <li className="ui-multiselect-optgroup-label">
                <a>{filterSeverityHeader}</a>
              </li>
            )}
            {severityFilters.map(renderMainRow)}

            {monitorTypeFilters.length > 0 && (
              <li className="ui-multiselect-optgroup-label">
                <a>{filterMonitorTypeHeader}</a>
              </li>
            )}
            {monitorTypeFilters.map(renderMainRow)}

            {extraFilters.map(renderMainRow)}
          </ul>
        </div>
      )}
    </div>
  );
}
